<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <RotateSquare size="40px" />
    </div>

    <div v-else>
      <p class="text-muted small mb-3">
        Formulário fixo de contribuições e sugestões da população, recebido durante a Audiência Pública
        (Capítulo 14). As perguntas não são editáveis.
      </p>

      <div class="row mb-4">
        <!-- Formulário público -->
        <div class="col-lg-6 mb-3 mb-lg-0">
          <div class="card bg-light border h-100">
            <div class="card-body d-flex flex-column">
              <h6 class="mb-3"><i class="fa fa-link mr-1"></i> Formulário de contribuições</h6>

              <div class="form-group mb-2">
                <label class="small font-weight-bold mb-1">
                  Status
                  <b-badge :variant="statusVariant(form.status)" class="ml-1">
                    {{ statusLabel(form.status) }}
                  </b-badge>
                </label>
                <div class="d-flex">
                  <b-form-select v-model="form.status" :options="statusOpcoes" size="sm" class="mr-2" />
                  <button class="btn btn-primary btn-sm flex-shrink-0" :disabled="salvandoStatus" @click="salvarStatus">
                    {{ salvandoStatus ? "..." : "Salvar" }}
                  </button>
                </div>
              </div>

              <div class="input-group input-group-sm mb-2">
                <b-form-input :value="publicUrl" readonly size="sm" />
                <div class="input-group-append">
                  <button class="btn btn-outline-secondary btn-sm" @click="copiarLink" title="Copiar link">
                    <i class="fa fa-copy"></i>
                  </button>
                  <a class="btn btn-outline-primary btn-sm" :href="publicUrl" target="_blank" title="Abrir formulário">
                    <i class="fa fa-external-link-alt"></i>
                  </a>
                </div>
              </div>
              <small class="text-muted mt-auto">
                Só aceita respostas quando o status estiver <strong>Aberto</strong>.
              </small>
            </div>
          </div>
        </div>

        <!-- Painel de acompanhamento + tempo de fala -->
        <div class="col-lg-6">
          <div class="card bg-light border h-100">
            <div class="card-body d-flex flex-column">
              <h6 class="mb-3"><i class="fa fa-desktop mr-1"></i> Painel de acompanhamento</h6>
              <p class="small text-muted mb-2">Telão do evento e acompanhamento pela população.</p>

              <div class="input-group input-group-sm mb-3">
                <b-form-input :value="painelUrl" readonly size="sm" />
                <div class="input-group-append">
                  <button class="btn btn-outline-secondary btn-sm" @click="copiarPainel" title="Copiar link do painel">
                    <i class="fa fa-copy"></i>
                  </button>
                  <a class="btn btn-outline-primary btn-sm" :href="painelUrl" target="_blank" title="Abrir painel">
                    <i class="fa fa-external-link-alt"></i>
                  </a>
                </div>
              </div>

              <div class="border-top pt-3">
                <label class="small font-weight-bold mb-1">
                  <i class="fa fa-clock mr-1"></i> Tempo de fala
                  <span class="text-muted font-weight-normal">(minutos por participante)</span>
                </label>
                <div class="d-flex align-items-center">
                  <b-form-input
                    v-model.number="tempoFala"
                    type="number"
                    min="1"
                    size="sm"
                    placeholder="Ex: 3"
                    class="mr-2"
                    style="max-width: 110px"
                  />
                  <button
                    class="btn btn-primary btn-sm flex-shrink-0 mr-3"
                    :disabled="salvando || !evento"
                    @click="salvarTempoFala"
                  >
                    {{ salvando ? "..." : "Salvar" }}
                  </button>
                  <small class="text-muted">
                    No painel: <strong>{{ tempoFalaFormatado }}</strong>
                  </small>
                </div>
              </div>

              <small class="text-muted mt-auto pt-2">
                O painel atualiza sozinho a cada poucos segundos durante a audiência.
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-3">
        Total: <strong class="text-primary">{{ totalRespostas }}</strong> contribuição(ões)
      </div>

      <div class="d-flex justify-content-between align-items-end flex-wrap mb-3" style="gap: 12px">
        <div class="d-flex align-items-end flex-wrap" style="gap: 12px">
          <div class="form-group mb-0">
            <label class="small text-muted mb-1 d-block">Situação</label>
            <b-form-select
              v-model="filtroSituacao"
              :options="filtroOpcoes"
              size="sm"
              style="width: 180px"
            />
          </div>
          <div class="form-group mb-0">
            <label class="small text-muted mb-1 d-block">Capítulo</label>
            <b-form-select
              v-model="filtroCapitulo"
              :options="capituloOpcoes"
              size="sm"
              style="width: 240px"
            />
          </div>
          <div class="form-group mb-0">
            <label class="small text-muted mb-1 d-block">Nome ou instituição</label>
            <b-form-input v-model="filtroBusca" size="sm" placeholder="Buscar..." style="width: 220px" />
          </div>
        </div>
        <div class="d-flex flex-shrink-0" style="gap: 8px">
          <button
            class="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
            :disabled="loadingRespostas"
            title="Reconsultar contribuições e contadores"
            @click="carregarRespostas"
          >
            <i class="fa fa-sync-alt mr-1" :class="{ 'fa-spin': loadingRespostas }"></i>
            {{ loadingRespostas ? "Atualizando..." : "Atualizar" }}
          </button>
          <button
            class="btn btn-success btn-sm d-inline-flex align-items-center"
            :disabled="exportandoExcel || stats.total === 0"
            @click="exportarExcel"
          >
            <i class="fa fa-file-excel mr-1"></i>
            {{ exportandoExcel ? "Exportando..." : "Exportar Excel" }}
          </button>
        </div>
      </div>

      <div v-if="loadingRespostas" class="text-center py-4">
        <RotateSquare size="40px" />
      </div>

      <div v-else-if="!respostas.length" class="text-muted small py-2">
        {{
          filtroSituacao || filtroCapitulo || filtroBusca
            ? "Nenhuma contribuição encontrada para os filtros selecionados."
            : "Nenhuma contribuição recebida ainda."
        }}
      </div>

      <div v-else>
        <div v-for="(r, i) in respostas" :key="r.id" class="card mb-2">
          <div class="card-body py-3 px-3">
            <div class="d-flex justify-content-between align-items-start">
              <p class="mb-0 font-weight-bold flex-grow-1 mr-3">
                {{ r.name || r.institution || ("Contribuição " + (skipRespostas + i + 1)) }}
                <span v-if="r.name && r.institution" class="text-muted font-weight-normal small">
                  · {{ r.institution }}
                </span>
              </p>
              <div class="flex-shrink-0 d-flex">
                <button
                  class="btn btn-outline-info btn-sm mr-1 d-inline-flex align-items-center"
                  @click="toggleDetalhe(r.id)"
                >
                  <i class="fa mr-1" :class="respostaExpandida === r.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  {{ respostaExpandida === r.id ? "Fechar" : "Ver respostas" }}
                </button>
                <button
                  class="btn btn-sm mr-1 d-inline-flex align-items-center"
                  :class="r.reviewed ? 'btn-outline-primary' : 'btn-primary'"
                  @click="abrirModalParecer(r)"
                >
                  <i class="fa fa-gavel mr-1"></i>
                  {{ r.reviewed ? "Editar parecer" : "Dar parecer" }}
                </button>
                <button
                  class="btn btn-outline-danger btn-sm d-inline-flex align-items-center"
                  :disabled="excluindoResposta === r.id"
                  @click="excluirResposta(r)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
            <div class="small mt-1">
              <div v-if="r.chapter" class="mb-1"><span class="text-muted">Capítulo:</span> {{ r.chapter }}</div>
              <div class="mb-1">
                <span class="text-muted">Situação:</span> <span :class="situacaoTextClass(r)">{{ situacaoLabel(r) }}</span>
              </div>
              <div>
                <span class="text-muted">Com parecer:</span> <span :class="r.reviewed ? 'font-weight-bold text-success' : 'font-weight-bold text-danger'">{{ r.reviewed ? "Sim" : "Não" }}</span>
              </div>
            </div>

            <!-- Detalhe expansível: data/hora, parecer completo e respostas ao formulário -->
            <div v-if="respostaExpandida === r.id" class="mt-3">
              <div class="small mb-2"><span class="text-muted">Data/hora:</span> {{ formatarData(r.submitted_at) }}</div>

              <div v-if="r.reviewed" class="border-left pl-3 mb-3" style="border-left-width:3px !important">
                <div class="text-uppercase text-muted" style="font-size:0.68rem;letter-spacing:0.06em">
                  <i class="fa fa-gavel mr-1"></i> Parecer do técnico
                </div>
                <div class="small" style="white-space:pre-line">{{ r.review_notes }}</div>
                <small class="text-muted">
                  {{ r.reviewed_by_name || "—" }} · {{ formatarData(r.reviewed_at) }}
                </small>
              </div>

              <div v-if="loadingDetalhe" class="text-center py-2">
                <RotateSquare size="30px" />
              </div>
              <template v-else-if="detalheResposta[r.id]">
                <div
                  v-for="(a, ai) in (detalheResposta[r.id].answers || [])"
                  :key="ai"
                  class="border-top pt-2 mt-2 small"
                >
                  <div class="font-weight-bold mb-1">{{ ai + 1 }}. {{ a.question_text }}</div>
                  <div v-if="a.question_type === 'free_text'" class="text-muted">{{ a.value || "—" }}</div>
                  <div v-else>
                    <div v-if="!a.value || !a.value.length" class="text-muted">—</div>
                    <div v-for="(opt, oi) in a.value" :key="oi" class="text-muted">{{ opt }}</div>
                    <div v-if="a.other_text" class="text-muted">Outro: {{ a.other_text }}</div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="totalRespostas > limitRespostas" class="d-flex justify-content-between align-items-center mt-3">
          <button class="btn btn-outline-secondary btn-sm" :disabled="skipRespostas === 0" @click="paginaAnterior">
            Anterior
          </button>
          <small class="text-muted">
            {{ skipRespostas + 1 }}–{{ Math.min(skipRespostas + limitRespostas, totalRespostas) }}
            de {{ totalRespostas }}
          </small>
          <button
            class="btn btn-outline-secondary btn-sm"
            :disabled="skipRespostas + limitRespostas >= totalRespostas"
            @click="proximaPagina"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: parecer do técnico ────────────────────────────────────── -->
    <b-modal
      v-model="modalParecer"
      title="Parecer sobre a contribuição"
      ok-title="Salvar parecer"
      cancel-title="Cancelar"
      :ok-disabled="salvandoParecer || !parecerValido"
      @ok="salvarParecer"
      @hidden="fecharModalParecer"
    >
      <div class="form-group">
        <label class="font-weight-bold d-block mb-2">Situação <span class="text-danger">*</span></label>
        <b-form-radio v-model="parecer.outcome" value="atendida" class="mb-1">
          Atendida
        </b-form-radio>
        <b-form-radio v-model="parecer.outcome" value="nao_atendida">
          Não atendida
        </b-form-radio>
      </div>
      <div v-if="respostaParecer && respostaParecer.is_speech_request" class="form-group">
        <label class="font-weight-bold">Descrição da fala</label>
        <b-form-textarea
          v-model="parecer.description"
          rows="3"
          placeholder="Registre o que foi apresentado oralmente pela pessoa..."
        />
        <small class="text-muted">
          Use este campo para transcrever a contribuição feita na fala, antes de emitir o parecer.
        </small>
      </div>
      <div class="form-group mb-0">
        <label class="font-weight-bold">
          Parecer descritivo <span class="text-danger">*</span>
        </label>
        <b-form-textarea
          v-model="parecer.notes"
          rows="5"
          placeholder="Descreva a análise da contribuição e a justificativa da decisão..."
        />
        <small class="text-muted">
          Obrigatório em ambos os casos — toda contribuição precisa ser respondida com justificativa.
        </small>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import SurveyService from "../../servico/SurveyService";

const STAGE_CODE = "public_hearing_suggestions";

export default {
  name: "PmauPublicHearingContributions",
  components: { RotateSquare },
  props: {
    contaId: {
      type: String,
      default: null
    },
    // O evento é do componente pai; aqui só exibimos o link do painel e
    // pedimos a alteração do tempo de fala (ver salvarTempoFala).
    evento: {
      type: Object,
      default: null
    },
    salvando: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      carregado: false,
      salvandoStatus: false,
      form: {
        id: null,
        status: "draft",
        public_token: ""
      },
      formId: null,

      statusOpcoes: [
        { value: "draft", text: "Rascunho" },
        { value: "active", text: "Aberto" },
        { value: "closed", text: "Encerrado" }
      ],

      loadingRespostas: false,
      respostas: [],
      totalRespostas: 0,
      skipRespostas: 0,
      limitRespostas: 10,
      respostaExpandida: null,
      loadingDetalhe: false,
      detalheResposta: {},
      excluindoResposta: null,

      stats: { total: 0, reviewed: 0, pending: 0 },
      filtroSituacao: null,
      filtroOpcoes: [
        { value: null, text: "Todas" },
        { value: "pendente", text: "Pendentes de parecer" },
        { value: "atendida", text: "Atendidas" },
        { value: "nao_atendida", text: "Não atendidas" }
      ],
      filtroBusca: "",
      filtroBuscaTimer: null,
      filtroCapitulo: null,
      capituloOpcoes: [{ value: null, text: "Todos os capítulos" }],

      tempoFala: null,

      modalParecer: false,
      salvandoParecer: false,
      respostaParecer: null,
      parecer: { outcome: "nao_atendida", notes: "", description: "" },

      exportandoExcel: false
    };
  },
  computed: {
    publicUrl() {
      if (!this.form.public_token) return "";
      const base = window.location.origin + window.location.pathname;
      return base + "#/audiencia-publica/sugestoes/" + this.form.public_token;
    },
    parecerValido() {
      return !!(this.parecer.notes && this.parecer.notes.trim());
    },
    painelUrl() {
      if (!this.evento || !this.evento.public_token) return "";
      const base = window.location.origin + window.location.pathname;
      return base + "#/audiencia-publica/painel/" + this.evento.public_token;
    },
    tempoFalaFormatado() {
      if (!this.tempoFala) return "—";
      return String(this.tempoFala).padStart(2, "0") + ":00";
    }
  },
  watch: {
    evento: {
      immediate: true,
      handler(ev) {
        this.tempoFala = ev ? ev.speaking_time_minutes : null;
      }
    },
    filtroSituacao() {
      this.skipRespostas = 0;
      this.carregarRespostas();
    },
    filtroCapitulo() {
      this.skipRespostas = 0;
      this.carregarRespostas();
    },
    filtroBusca() {
      // Debounce — evita 1 requisição por tecla digitada.
      clearTimeout(this.filtroBuscaTimer);
      this.filtroBuscaTimer = setTimeout(() => {
        this.skipRespostas = 0;
        this.carregarRespostas();
      }, 400);
    }
  },
  methods: {
    carregar() {
      if (this.carregado) return;
      this.carregado = true;
      this.carregarFormulario();
    },
    carregarFormulario() {
      if (!this.contaId) return;
      this.loading = true;
      SurveyService.GetForm(this.contaId, STAGE_CODE)
        .then((r) => {
          this.form = r.data;
          this.formId = r.data.id;
          this.carregarRespostas();
          this.carregarCapitulos();
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar formulário de contribuições."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    carregarCapitulos() {
      SurveyService.ListBaseQuestions(STAGE_CODE)
        .then((r) => {
          const capituloQ = (r.data || []).find((q) => q.code === "ph_capitulo");
          const opcoes = (capituloQ ? capituloQ.options : [])
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((o) => ({ value: o.id, text: o.option_text }));
          this.capituloOpcoes = [{ value: null, text: "Todos os capítulos" }, ...opcoes];
        })
        .catch(() => {});
    },
    salvarStatus() {
      if (!this.formId) return;
      this.salvandoStatus = true;
      SurveyService.UpdateForm(this.formId, { status: this.form.status })
        .then((r) => {
          this.form.status = r.data.status;
          this.$notify({ data: ["Status atualizado com sucesso."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar status."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.salvandoStatus = false;
        });
    },
    salvarTempoFala() {
      this.$emit("salvar-tempo-fala", this.tempoFala);
    },
    copiarPainel() {
      if (!this.painelUrl) return;
      navigator.clipboard.writeText(this.painelUrl).then(() => {
        this.$notify({ data: ["Link do painel copiado!"], type: "success", duration: 2000 });
      });
    },
    copiarLink() {
      if (!this.publicUrl) return;
      navigator.clipboard.writeText(this.publicUrl).then(() => {
        this.$notify({ data: ["Link copiado para a área de transferência!"], type: "success", duration: 2000 });
      });
    },
    statusLabel(s) {
      return { draft: "Rascunho", active: "Aberto", closed: "Encerrado" }[s] || s;
    },
    statusVariant(s) {
      return { draft: "secondary", active: "success", closed: "danger" }[s] || "secondary";
    },
    formatarData(iso) {
      return iso ? new Date(iso).toLocaleString("pt-BR") : "";
    },

    // ── Parecer do técnico ─────────────────────────────────────────────────
    situacaoLabel(r) {
      return r.review_outcome === "atendida" ? "Atendida" : "Não atendida";
    },
    situacaoTextClass(r) {
      return r.review_outcome === "atendida" ? "font-weight-bold text-success" : "font-weight-bold text-danger";
    },
    abrirModalParecer(r) {
      this.respostaParecer = r;
      this.parecer = {
        outcome: r.review_outcome || "nao_atendida",
        notes: r.review_notes || "",
        description: r.description || ""
      };
      this.modalParecer = true;
    },
    fecharModalParecer() {
      this.respostaParecer = null;
      this.parecer = { outcome: "nao_atendida", notes: "", description: "" };
    },
    salvarParecer(evt) {
      evt.preventDefault();
      if (!this.parecerValido || !this.respostaParecer) {
        this.$notify({ data: ["Informe o parecer descritivo."], type: "warn", duration: 3000 });
        return;
      }
      const alvo = this.respostaParecer;
      this.salvandoParecer = true;

      // Em solicitações de fala o técnico transcreve o que foi dito antes de
      // emitir o parecer — a descrição vai primeiro, o parecer depois.
      const descricaoAlterada =
        alvo.is_speech_request && (this.parecer.description || "") !== (alvo.description || "");
      const passoDescricao = descricaoAlterada
        ? SurveyService.UpdateResponseDescription(alvo.id, this.parecer.description || "")
        : Promise.resolve(null);

      passoDescricao
        .then(() => SurveyService.ReviewResponse(alvo.id, this.parecer.outcome, this.parecer.notes))
        .then((r) => {
          const idx = this.respostas.findIndex((x) => x.id === alvo.id);
          if (idx !== -1) {
            this.$set(this.respostas, idx, {
              ...this.respostas[idx],
              ...r.data,
              description: this.parecer.description || this.respostas[idx].description
            });
          }
          // O detalhe expandido guarda uma cópia dos mesmos campos
          if (this.detalheResposta[alvo.id]) {
            this.$set(this.detalheResposta, alvo.id, { ...this.detalheResposta[alvo.id], ...r.data });
          }
          this.modalParecer = false;
          this.$notify({ data: ["Parecer registrado com sucesso."], type: "success", duration: 3000 });
          // Recarrega para atualizar contadores e respeitar o filtro ativo
          this.carregarRespostas();
        })
        .catch((e) => {
          const msg = e?.response?.data?.detail || "Erro ao salvar parecer.";
          this.$notify({ data: [msg], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.salvandoParecer = false;
        });
    },

    // ── Respostas ──────────────────────────────────────────────────────────
    carregarRespostas() {
      if (!this.formId) return;
      this.loadingRespostas = true;
      SurveyService.ListResponses(
        this.formId,
        this.skipRespostas,
        this.limitRespostas,
        this.filtroSituacao,
        this.filtroBusca,
        this.filtroCapitulo
      )
        .then((r) => {
          this.respostas = r.data.items;
          this.totalRespostas = r.data.total;
          if (r.data.stats) this.stats = r.data.stats;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar contribuições."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.loadingRespostas = false;
        });
    },
    proximaPagina() {
      this.skipRespostas += this.limitRespostas;
      this.carregarRespostas();
    },
    paginaAnterior() {
      this.skipRespostas = Math.max(0, this.skipRespostas - this.limitRespostas);
      this.carregarRespostas();
    },
    toggleDetalhe(responseId) {
      if (this.respostaExpandida === responseId) {
        this.respostaExpandida = null;
        return;
      }
      this.respostaExpandida = responseId;
      if (this.detalheResposta[responseId]) return;
      this.loadingDetalhe = true;
      SurveyService.GetResponse(responseId)
        .then((r) => {
          this.$set(this.detalheResposta, responseId, r.data);
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar detalhe da contribuição."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.loadingDetalhe = false;
        });
    },
    excluirResposta(r) {
      if (!confirm("Deseja excluir esta contribuição? A ação pode ser revertida apenas por administrador de banco de dados.")) return;
      this.excluindoResposta = r.id;
      SurveyService.DeleteResponse(r.id)
        .then(() => {
          this.respostas = this.respostas.filter((x) => x.id !== r.id);
          this.totalRespostas = Math.max(0, this.totalRespostas - 1);
          if (this.respostaExpandida === r.id) this.respostaExpandida = null;
          this.$notify({ data: ["Contribuição excluída."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao excluir contribuição."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.excluindoResposta = null;
        });
    },

    // ── Export ─────────────────────────────────────────────────────────────
    exportarExcel() {
      if (!this.formId) return;
      this.exportandoExcel = true;
      SurveyService.ExportResponses(this.formId)
        .then((r) => {
          const blob = new Blob([r.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "audiencia-publica-contribuicoes.xlsx";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao exportar contribuições."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.exportandoExcel = false;
        });
    }
  }
};
</script>
